import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { booleanScheme } from 'src/utils/validation-schemes';
import { Button } from 'src/components/ui/button';
import {
  useRunKnowledgeBaseCollectorMutation,
  useSetupKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useParams } from 'react-router-dom';
import { useNotification } from 'src/hooks/useNotification';
import { Switch } from 'src/components/ui/switch';
import { EditKnowledgeBaseIntegrationModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/edit/EditKnowledgeBaseIntegrationModal/EditKnowledgeBaseIntegrationModal';
import { useAppSelector } from 'src/store';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import type { TreeNode } from 'src/utils/fileUtils';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useIsCosupportManager } from 'src/hooks/useIsCosupportManager';

export const SyncKnowledgeBaseZendeskHelpCenterForm = ({
  item,
  close = () => {},
}: {
  // A knowledge base row; its collector is in `data`.
  item: TreeNode;
  close?: () => void;
}) => {
  const [integration, setIntegration] = useState(item);
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const [isCredentialsEmpty, setIsCredentialsEmpty] = useState(
    !integration.data.zendesk_subdomain ||
      !integration.data.domain ||
      !integration.data.zendesk_email ||
      !integration.data.brand_id,
  );
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const { id } = useParams();
  const notification = useNotification();
  const isCosupportManager = useIsCosupportManager();
  const [runCollector, { isLoading: isLoadingRunCollector, isSuccess: isSuccessRunCollector }] =
    useRunKnowledgeBaseCollectorMutation();
  const [setupCollector, { isLoading: isLoadingSetupCollector }] =
    useSetupKnowledgeBaseCollectorMutation();
  const [
    updateCollector,
    { isLoading: isLoadingUpdateCollector, isSuccess: isSuccessUpdateCollector },
  ] = useUpdateKnowledgeBaseCollectorMutation();

  useEffect(() => {
    if (isSuccessRunCollector) {
      notification.success(`${item.name} Resync Started Successfully.`);
      close();
    }
  }, [isSuccessRunCollector]);

  useEffect(() => {
    if (isSuccessUpdateCollector) {
      notification.success(`${item.name} Updated Successfully.`);

      !enabled && close();
    }
  }, [isSuccessUpdateCollector]);

  useLoaderWhile(isLoadingRunCollector, isLoadingSetupCollector, isLoadingUpdateCollector);

  useEffect(() => {
    if (item) {
      setIntegration(item);
      setIsCredentialsEmpty(
        !item.data.zendesk_subdomain ||
          !item.data.domain ||
          !item.data.zendesk_email ||
          !item.data.brand_id,
      );
    }
  }, [item]);

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      enabled: integration.data.enabled,
    },
    resolver: yupResolver(
      object({
        enabled: booleanScheme({ required: false }),
      }),
    ),
  });

  const submit = () => {
    const body = {
      type: item.data.type,
      name: `[CoSupportAI] Article Auto-Sync (${item.data.zendesk_subdomain})`,
    };

    updateCollector({
      knowledgeBaseId: id,
      collectorId: item.id,
      body: { enabled, type: item.data.type },
    });

    if (enabled) {
      setupCollector({ knowledgeBaseId: id, collectorId: item.id, body }).then(
        (setupCollectorResult) => {
          if (!('error' in setupCollectorResult)) {
            notification.success(`${item.name} Auto Setup Started Successfully.`);
            close();
          }
        },
      );
    }
  };

  const resync = (e: React.MouseEvent) => {
    e.preventDefault();

    runCollector({ knowledgeBaseId: id, collectorId: item.id });
  };

  const enabled = watch('enabled');

  useEffect(() => {
    if (enabled && item.data) {
      if (isCredentialsEmpty) {
        setIntegration({ ...integration, data: { ...integration.data, enabled } });
        setIsEditPopupOpen(true);
      }
    } else {
      setIsEditPopupOpen(false);
    }
  }, [enabled]);

  return (
    <>
      <EditKnowledgeBaseIntegrationModal
        item={integration}
        isOpen={isEditPopupOpen}
        hideTrigger={true}
      />
      <form
        onSubmit={handleSubmit(submit)}
        id="sync-zendesk-integration"
        className={'h-full flex flex-col justify-between'}
      >
        <div className="flex flex-col gap-2.5">
          <div className={'flex flex-col gap-[5px]'}>
            <Controller
              control={control}
              name="enabled"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <span className="flex flex-col gap-4">
                  <span className="flex items-center gap-4">
                    <Switch
                      type="black"
                      checked={value}
                      onCheckedChange={onChange}
                      disabled={isTrialPlan}
                    />
                    <div className={'flex flex-col'}>
                      <span
                        className={
                          'text-ebony_opacity_80 font-semibold font-jamjuree text-base flex flex-row items-center gap-2'
                        }
                      >
                        Real-time sync
                        <InputInfoToolTip
                          infoToolTip={{
                            side: 'right',
                            description: isTrialPlan ? (
                              <>Unavailable for trial plan</>
                            ) : (
                              <>Sync new content and updates in real time</>
                            ),
                          }}
                        />
                      </span>
                      <span className={'text-ebony_opacity_50 font-medium font-jamjuree text-base'}>
                        (Sync new content and updates in real time)
                      </span>
                    </div>
                  </span>
                </span>
              )}
            />
          </div>
        </div>
        <div className="flex justify-center mt-5">
          <div className="flex gap-2.5">
            <Button variant="outline" type="button" className="min-w-24" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" className="min-w-24" disabled={isCredentialsEmpty}>
              <span>Update</span>
            </Button>
            {isCosupportManager && (
              <Button onClick={resync} variant="outline-light" className="min-w-24">
                <span>One-time resync</span>
              </Button>
            )}
          </div>
        </div>
      </form>
    </>
  );
};
